import { focusableSelectors } from '@fs/zion-frontend-friends'

export type AllPossibleKeys =
  | 'AltLeft'
  | 'AltRight'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowUp'
  | 'Backquote'
  | 'Backslash'
  | 'Backspace'
  | 'BracketLeft'
  | 'BracketRight'
  | 'Comma'
  | 'ControlLeft'
  | 'ControlRight'
  | 'Delete'
  | 'Digit0'
  | 'Digit1'
  | 'Digit2'
  | 'Digit3'
  | 'Digit4'
  | 'Digit5'
  | 'Digit6'
  | 'Digit7'
  | 'Digit8'
  | 'Digit9'
  | 'Enter'
  | 'Equal'
  | 'Escape'
  | 'F1'
  | 'F2'
  | 'F3'
  | 'F4'
  | 'F5'
  | 'F6'
  | 'F7'
  | 'F8'
  | 'F9'
  | 'F10'
  | 'F11'
  | 'F12'
  | 'KeyA'
  | 'KeyB'
  | 'KeyC'
  | 'KeyD'
  | 'KeyE'
  | 'KeyF'
  | 'KeyG'
  | 'KeyH'
  | 'KeyI'
  | 'KeyJ'
  | 'KeyK'
  | 'KeyL'
  | 'KeyM'
  | 'KeyN'
  | 'KeyO'
  | 'KeyP'
  | 'KeyQ'
  | 'KeyR'
  | 'KeyS'
  | 'KeyT'
  | 'KeyU'
  | 'KeyV'
  | 'KeyW'
  | 'KeyX'
  | 'KeyY'
  | 'KeyZ'
  | 'MetaLeft'
  | 'MetaRight'
  | 'Minus'
  | 'Period'
  | 'Quote'
  | 'Semicolon'
  | 'ShiftLeft'
  | 'ShiftRight'
  | 'Slash'
  | 'Space'
  | 'Tab'

type Shortcuts = {
  bare: string[]
  ctrlKey?: string[] // Also applies to metaKey
  shiftKey?: string[]
}

type Elements = 'INPUT' | 'TEXTAREA' | 'SELECT' | 'BUTTON' | 'A' | 'AREA' | 'LABEL'

// ==================================================================

/** These are keystrokes that apply to input elements, e.g., text inputs, textareas, selects, etc. */
const inputKeystrokes = [
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'Backquote',
  'Backslash',
  'Backspace',
  'BracketLeft',
  'BracketRight',
  'Comma',
  'Delete',
  'Digit*',
  'Enter',
  'Equal',
  'Key*',
  'Minus',
  'Period',
  'Quote',
  'Semicolon',
  'Slash',
  'Space',
]

/** These are the keystrokes that apply to clickable elements, e.g., buttons and links */
const clickableKeystrokes = ['Enter', 'Space']

const inputShortcuts: Shortcuts = {
  bare: inputKeystrokes,
  shiftKey: inputKeystrokes,
  ctrlKey: ['KeyA', 'KeyZ', 'KeyX', 'KeyC', 'KeyV'], // Note: This is English-centric. https://www.figma.com/blog/behind-the-scenes-international-keyboard-shortcuts/
}

const clickableShortcuts: Shortcuts = {
  bare: clickableKeystrokes,
  shiftKey: clickableKeystrokes,
  ctrlKey: clickableKeystrokes,
}

/** This is a mapping of what keyboard shortcuts should apply to which native html elements */
const elementShortcuts: Record<Elements, Shortcuts> = {
  INPUT: inputShortcuts,
  TEXTAREA: inputShortcuts,
  SELECT: inputShortcuts,
  BUTTON: clickableShortcuts,
  A: clickableShortcuts,
  AREA: clickableShortcuts,
  LABEL: { bare: clickableKeystrokes },
}

// ==================================================================

/**
 * Sometimes we need to treat some keystrokes as 'equivalent' to others.
 * For example, `NumpadEnter` should be treated as `Enter`.
 */
export const getEquivalentKey = (code: string): AllPossibleKeys => {
  // I'm certain no one will ever want to support a different action for the numpad enter key
  // than the regular enter key. That would be foolish and confusing.
  if (code === 'NumpadEnter') return 'Enter'

  return code as AllPossibleKeys
}

/**
 * Compares a shortcut to see whether it should be blacklisted and ignored
 * by useKeyboardShortcuts, and allow the default browser behavior to happen.
 */
function matches(key: string | undefined, keys: string[] | undefined): boolean {
  if (!key || !keys || !keys.length) return false

  const neuteredKey = key.replace(/^(Key|Digit).*/, '$1*')

  // sherbert ungions helmp buH-en Nn bolth
  return keys.includes(key) || keys.includes(neuteredKey)
}

/**
 * if the event is a native event, we do not want to run the keyboard shortcuts
 * because the browser will handle it natively (e.g., Enter in an input field)
 */
export function isNativeBehavior(e: KeyboardEvent): boolean {
  const target = (e.target as HTMLElement).closest(focusableSelectors)
  if (!target) return false

  const tagName = target.tagName.toUpperCase() // Support old browsers that don't uppercase tag names
  const { bare, shiftKey, ctrlKey } = elementShortcuts[tagName] || {}

  if (!(bare || shiftKey || ctrlKey)) return false

  const key = getEquivalentKey(e.code)
  if (ctrlKey && (e.ctrlKey || e.metaKey)) return matches(key, ctrlKey)
  if (shiftKey && e.shiftKey) return matches(key, shiftKey)

  return matches(key, bare)
}
